import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useBrand } from "@/context/BrandContext";
import { ShieldCheck, ScanLine } from "lucide-react";
import { GoogleButton } from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const { brand, logoSrc } = useBrand();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Signed in");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0b0f17]">
      {/* Left brand panel */}
      <div className="hidden lg:flex relative flex-col justify-between p-12 border-r border-slate-800/80 grid-bg">
        <div className="flex items-center gap-4">
          <img src={logoSrc} alt={brand.product_name} className="w-56 h-56 object-contain mix-blend-screen -ml-6" data-testid="login-logo" />
          <div data-testid="login-brand-text">
            <div className="font-heading text-3xl xl:text-4xl font-extrabold tracking-tight text-slate-100">{brand.product_name}</div>
            <div className="mt-2 text-base xl:text-lg font-medium text-amber-400 leading-snug max-w-sm">{brand.tagline}</div>
          </div>
        </div>

        <div className="max-w-md">
          <div className="label-cap mb-3 text-amber-500">// PROJECT MANUAL SCANNER</div>
          <h1 className="font-heading text-4xl xl:text-5xl font-extrabold text-slate-100 leading-tight">
            Every clause. Every spec. <span className="text-amber-500">Every risk.</span>
          </h1>
          <p className="mt-6 text-slate-400 leading-relaxed">
            AI-driven contract, specification, and AISC/AWS compliance review purpose-built for structural steel fabricators, estimators, and PMs.
          </p>

          <div className="mt-8 space-y-3">
            <div className="flex items-start gap-3 text-sm text-slate-300">
              <ScanLine className="w-4 h-4 mt-0.5 text-amber-400" />
              <span>CSI Div 05 & 10 priority scan — full-manual cross-reference</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-300">
              <ShieldCheck className="w-4 h-4 mt-0.5 text-amber-400" />
              <span>Custom playbooks, source-cited findings, exportable PM reports</span>
            </div>
          </div>
        </div>

        <div className="text-xs font-mono text-slate-600">v1.0 · Precision Fabrication Intelligence</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-md sc-card p-8" data-testid="login-form">
          <div className="mb-6">
            <div className="label-cap text-amber-500">// SIGN IN</div>
            <h2 className="font-heading text-2xl font-bold text-slate-100 mt-1">Enter workspace</h2>
            <p className="text-sm text-slate-400 mt-1">Access your steel review environment.</p>
          </div>
          <label className="block mb-4">
            <span className="label-cap">Email</span>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 focus:outline-none focus:border-amber-500"
              data-testid="login-email-input"
            />
          </label>
          <label className="block mb-6">
            <span className="label-cap flex justify-between">Password <Link to="/forgot-password" className="text-amber-400 hover:text-amber-300 normal-case tracking-normal" data-testid="forgot-password-link">Forgot password?</Link></span>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full bg-slate-950/60 border border-slate-800 rounded-md px-3 py-2.5 text-slate-100 focus:outline-none focus:border-amber-500"
              data-testid="login-password-input"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold py-2.5 rounded-md transition disabled:opacity-50"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="my-4 flex items-center gap-3 text-[10px] font-mono text-slate-500"><span className="flex-1 h-px bg-slate-800" />OR<span className="flex-1 h-px bg-slate-800" /></div>
          <GoogleButton label="Sign in with Google" />
          <div className="mt-4 text-sm text-slate-400 text-center">
            No account?{" "}
            <Link to="/register" className="text-amber-400 hover:text-amber-300" data-testid="login-to-register-link">
              Create workspace
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
